import type { ParticipantFieldGroupTemplate, Template, Y } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { useImageUpload } from '../../../components/tiptap/hooks/useImageUpload';
import {
  getParticipantDocumentTemplateExtensions,
  getTemplateVariables,
} from '../../../components/tiptap/utils';
import { useParticipantFieldGroupTemplatesQuery } from '../../../graphql/hooks/useParticipantFieldGroupTemplatesQuery';
import { useAppContext } from '../../../hooks/useAppContext';
import { TemplateScoringChecklistSidebar } from '../scoring/TemplateScoringChecklistSidebar';
import { TemplateEditor } from '../TemplateEditor';
import { ParticipantDocumentScoringEditor } from './ParticipantDocumentScoringEditor';
import { ParticipantDocumentTemplateEditorTitle } from './ParticipantDocumentTemplateEditorTitle';

interface Props {
  template: Template;
}

export const ParticipantDocumentTemplateEditor = (props: Props) => {
  const { template } = props;

  const [tab, onTabChange] = React.useState<'document' | 'scoring'>('document');

  const { organization, config } = useAppContext();

  const query = useParticipantFieldGroupTemplatesQuery({
    organizationId: organization.id,
  });

  const variables = React.useMemo(() => {
    const fieldGroupTemplates =
      (query.data?.organization
        ?.participantFieldGroupTemplates as ParticipantFieldGroupTemplate[]) ?? [];

    return getTemplateVariables(fieldGroupTemplates);
  }, [query.data?.organization?.participantFieldGroupTemplates]);

  const onImageUpload = useImageUpload({
    imageUploadUrl: `${config.api.webUrl}/organizations/${organization.id}/templates/${template.id}/images`,
  });

  const extensions = getParticipantDocumentTemplateExtensions({ variables, onImageUpload });

  const { mainContent, toolbarContent, sidebarContent } = P.run(() => {
    switch (tab) {
      case 'document':
        return {
          mainContent: undefined,
          toolbarContent: undefined,
          sidebarContent: undefined,
        };
      case 'scoring':
        return {
          mainContent: (_editor: Editor, yDoc: Y.Doc) => (
            <ParticipantDocumentScoringEditor yDoc={yDoc} />
          ),
          toolbarContent: () => null,
          sidebarContent: (_editor: Editor, yDoc: Y.Doc) => (
            <TemplateScoringChecklistSidebar yDoc={yDoc} />
          ),
        };
    }
  });

  return (
    <ChakraV3.Box borderTopWidth='1px' borderColor='gray.200' h='full'>
      <TemplateEditor
        documentId={`template/${template.id}`}
        extensions={extensions}
        isEditable
        mainContent={mainContent}
        toolbarContent={toolbarContent}
        sidebarContent={sidebarContent}
        title={(options) => (
          <ParticipantDocumentTemplateEditorTitle
            template={template}
            onTabChange={onTabChange}
            {...options}
          />
        )}
      />
    </ChakraV3.Box>
  );
};
